import type { EntitlementType, EntitlementValue } from "@enduring-systems/entitlements";

import { BooleanControl } from "./BooleanControl";
import { ConfigControl } from "./ConfigControl";
import { EnumControl } from "./EnumControl";
import { FallbackControl } from "./FallbackControl";
import { QuotaControl } from "./QuotaControl";

export interface EntitlementControlProps {
  readonly capabilityId: string;
  readonly type: EntitlementType;
  readonly value: EntitlementValue;
  readonly onChange: (value: EntitlementValue) => void;
  readonly disabled?: boolean;
}

export function EntitlementControl({
  capabilityId,
  type,
  value,
  onChange,
  disabled = false,
}: EntitlementControlProps): JSX.Element {
  switch (type.kind) {
    case "boolean":
      if (value.kind !== "boolean") {
        return <FallbackControl value={value} />;
      }
      return (
        <BooleanControl
          key={capabilityId}
          capabilityId={capabilityId}
          disabled={disabled}
          value={value.value}
          onChange={(next) => onChange({ kind: "boolean", value: next })}
        />
      );
    case "quota":
      if (value.kind !== "quota") {
        return <FallbackControl value={value} />;
      }
      return (
        <QuotaControl
          key={capabilityId}
          capabilityId={capabilityId}
          disabled={disabled}
          value={value.value}
          onChange={(next) => onChange({ kind: "quota", value: next })}
        />
      );
    case "enum":
      if (value.kind !== "enum" || !type.values.includes(value.value)) {
        return <FallbackControl value={value} />;
      }
      return (
        <EnumControl
          key={capabilityId}
          capabilityId={capabilityId}
          disabled={disabled}
          values={type.values}
          value={value.value}
          onChange={(next) => onChange({ kind: "enum", value: next })}
        />
      );
    case "config":
      if (value.kind !== "config") {
        return <FallbackControl value={value} />;
      }
      return (
        <ConfigControl
          key={capabilityId}
          capabilityId={capabilityId}
          disabled={disabled}
          type={type}
          value={value.value}
          onChange={(next) => onChange({ kind: "config", value: next })}
        />
      );
    default:
      return <FallbackControl value={value} />;
  }
}
